import {
  getRecord,
  idbRequest,
  withOfflineTransaction,
} from './db';

import { getDeviceId } from './device';
import type { LocalAuditEvent } from './types';

const appVersion = () =>
  (import.meta as any).env?.VITE_APP_VERSION || '1.5.0-web';

/**
 * Allocate the next authoritative local audit sequence.
 *
 * Audit sequence is GLOBAL per tenant + authenticated user.
 *
 * It must never be derived from a domain-specific counter such as:
 * - GPS sequence
 * - command sequence
 * - harvest sequence
 * - mapping sequence
 *
 * Self-healing:
 * The metadata counter is compared with the highest persisted audit-event
 * sequence so stale/restored metadata cannot reuse an existing unique key.
 *
 * Caller MUST include both `meta` and `audit_events` in the same readwrite
 * transaction.
 */
export async function nextAuditSequence(
  tx: IDBTransaction,
  tenantId: string,
  userId: string
): Promise<number> {
  const meta = tx.objectStore('meta');
  const auditStore = tx.objectStore('audit_events');

  const sequenceKey = `audit_sequence:${userId}`;
  const current: any = await idbRequest(meta.get(sequenceKey));

  const metadataSequence = Math.max(
    0,
    Number(current?.value || 0)
  );

  let persistedSequence = 0;

  if (
    Array.from(auditStore.indexNames)
      .includes('byTenantUserSequence')
  ) {
    const index =
      auditStore.index('byTenantUserSequence');

    const range = IDBKeyRange.bound(
      [tenantId, userId, 0],
      [tenantId, userId, Number.MAX_SAFE_INTEGER]
    );

    const cursor: IDBCursorWithValue | null =
      await new Promise((resolve, reject) => {
        const request =
          index.openCursor(range, 'prev');

        request.onsuccess = () =>
          resolve(request.result);

        request.onerror = () =>
          reject(request.error);
      });

    if (cursor?.value) {
      persistedSequence = Math.max(
        0,
        Number(cursor.value.sequence || 0)
      );
    }
  }

  const sequence =
    Math.max(
      metadataSequence,
      persistedSequence
    ) + 1;

  await idbRequest(
    meta.put({
      key: sequenceKey,
      value: sequence,
      updated_at: new Date().toISOString(),
    })
  );

  return sequence;
}

export async function appendAudit(
  input: Omit<
    LocalAuditEvent,
    | 'audit_id'
    | 'device_id'
    | 'device_timestamp'
    | 'sequence'
    | 'app_version'
  >
): Promise<LocalAuditEvent> {
  return withOfflineTransaction(
    ['meta', 'audit_events'],
    'readwrite',
    async tx => {
      const sequence =
        await nextAuditSequence(
          tx,
          input.tenant_id,
          input.user_id
        );

      const event: LocalAuditEvent = {
        ...input,
        audit_id: crypto.randomUUID(),
        device_id: getDeviceId(),
        device_timestamp:
          new Date().toISOString(),
        sequence,
        app_version: appVersion(),
      };

      await idbRequest(
        tx.objectStore('audit_events').add(event)
      );

      return event;
    }
  );
}

export async function lastAuditSequence(
  userId: string
): Promise<number> {
  const value: any =
    await getRecord(
      'meta',
      `audit_sequence:${userId}`
    );

  return Number(value?.value || 0);
}
